import { JobApplicantStatus } from '@factoryfixinc/ats-interfaces';

import { APPLICATION_STATUS_FUNNEL_ORDER } from './global-search-labels.util';
import type {
  ConversationIndex,
  GlobalSearchApplicationRow,
  GlobalSearchCandidateGroup,
  GlobalSearchPartition,
} from './conversation-index.type';

/**
 * Pure helpers for the account-wide candidate search (ONE-6912 "Jump to any
 * candidate"). Kept outside the service class so components and specs can use
 * them without touching store/persistence wiring.
 */

/** Maximum cross-job application rows rendered by the account-wide search. */
export const GLOBAL_SEARCH_ROW_CAP = 25;

/** Minimum characters for a text query to trigger the account-wide search. */
export const GLOBAL_SEARCH_MIN_TEXT_LENGTH = 2;

/** Minimum digits for a phone-like query to trigger the account-wide search. */
export const GLOBAL_SEARCH_MIN_PHONE_DIGITS = 4;

/**
 * A query made of digits with optional punctuation/spaces is a phone lookup
 * ("630-270" is, "Marcus" is not). Mirrors the backend's numeric branch.
 */
export function isPhoneLikeQuery(query: string): boolean {
  const trimmed = query.trim();
  return /^[\d\s().+-]+$/.test(trimmed) && /\d/.test(trimmed);
}

/**
 * Digit-normalizes a phone-like query for the request: strips punctuation and
 * drops a leading US country code from an 11-digit number so a pasted E.164
 * value (`+1 (415) 555-0100`) can match 10-digit stored numbers. Sending
 * digits only also keeps older backends — whose phone branch requires a
 * digit-only query — able to serve punctuated phone searches.
 */
export function normalizePhoneQueryDigits(query: string): string {
  const digits = query.replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
}

/**
 * Minimum query before the account-wide search fires: 2 characters for text,
 * 4 digits for a phone-like query. Below the floor nothing is requested and
 * nothing is rendered — not even an empty state.
 */
export function isEligibleGlobalSearchQuery(query: string): boolean {
  const trimmed = query.trim();
  if (!trimmed) return false;
  if (isPhoneLikeQuery(trimmed)) {
    return trimmed.replace(/\D/g, '').length >= GLOBAL_SEARCH_MIN_PHONE_DIGITS;
  }
  return trimmed.length >= GLOBAL_SEARCH_MIN_TEXT_LENGTH;
}

/**
 * Partitions account-wide results into the selected job (across every
 * status/stage) and every other job, one row per application, grouped by
 * candidate in the cross-job zone and capped at `cap` rows. Applications in
 * pre-pipeline statuses (NOTIFIED / NOT_INTERESTED) are excluded — they have
 * no stage to reveal, mirroring Candidate Archive's valid-application rule.
 */
export function partitionGlobalSearchResults(
  results: ConversationIndex[],
  currentJobId: number | undefined,
  cap: number = GLOBAL_SEARCH_ROW_CAP,
): GlobalSearchPartition {
  const currentJobRows: GlobalSearchApplicationRow[] = [];
  const groups: GlobalSearchCandidateGroup[] = [];

  for (const conversationIndex of results) {
    const otherRows: GlobalSearchApplicationRow[] = [];

    for (const application of conversationIndex.applicationIndexes ?? []) {
      if (
        application.applicationStatus === JobApplicantStatus.NOTIFIED ||
        application.applicationStatus === JobApplicantStatus.NOT_INTERESTED
      ) {
        continue;
      }

      const row: GlobalSearchApplicationRow = { conversationIndex, application };
      if (currentJobId !== undefined && application.jobId === currentJobId) {
        currentJobRows.push(row);
      } else {
        otherRows.push(row);
      }
    }

    if (otherRows.length > 0) {
      // Newest application activity first within the candidate's group.
      otherRows.sort((a, b) => {
        const tsA = a.application.applicationApplyTs || a.application.updateTs || '';
        const tsB = b.application.applicationApplyTs || b.application.updateTs || '';
        return tsB.localeCompare(tsA);
      });
      groups.push({ conversationIndex, rows: otherRows, totalRows: otherRows.length });
    }
  }

  // Current-job rows follow the pane's own funnel order so a result sits
  // where the recruiter expects that person to sit. Unknown/legacy statuses
  // (indexOf miss) sort last, never above the funnel.
  const funnelRank = (status?: string | null): number => {
    const index = APPLICATION_STATUS_FUNNEL_ORDER.indexOf(status ?? '');
    return index === -1 ? APPLICATION_STATUS_FUNNEL_ORDER.length : index;
  };
  currentJobRows.sort(
    (a, b) =>
      funnelRank(a.application.applicationStatus) - funnelRank(b.application.applicationStatus),
  );

  // The cap counts rows, not candidates, and groups are rebuilt so a
  // truncated candidate never loses their name header.
  let budget = cap;
  let hiddenRows = 0;
  const cappedGroups: GlobalSearchCandidateGroup[] = [];
  for (const group of groups) {
    if (budget <= 0) {
      hiddenRows += group.rows.length;
      continue;
    }
    const taken = group.rows.slice(0, budget);
    hiddenRows += group.rows.length - taken.length;
    budget -= taken.length;
    cappedGroups.push({ ...group, rows: taken });
  }

  return {
    currentJobRows,
    otherJobGroups: cappedGroups,
    otherJobsShownCount: cap - budget,
    hasHiddenRows: hiddenRows > 0,
  };
}
